import type { PropertiesResponse } from "../api";
import { clearPersistedPins } from "./pinStore";

// propertiesCache — the last few county property responses, kept in memory so
// switching back to a county you already opened renders instantly instead of
// showing the skeleton and waiting on the network again (load-time fix,
// 2026-09-16). The dashboard still refetches in the background on every visit
// and swaps the fresh rows in, so a cached entry is only ever shown briefly.
//
// MEMORY ONLY, deliberately — never localStorage or sessionStorage. These rows
// carry plan-gated owner and mailing data (unlocked per county, per plan), so
// persisting them would leave one user's unlocked data on disk for the next
// person at a shared computer. A full page reload therefore starts empty, and
// so does every Stripe checkout or customer-portal return, which is how plan
// changes arrive: the cache can never outlive the plan it was filled under.
// (The slim map-pin payload, which carries none of that data, does persist
// across reloads: see pinStore.ts.)
//
// Keyed by user id + county. Anything else that changes what the server would
// return for the same user (the resolved-rows display toggle, a county-picker
// save) must call clearPropertiesCache(). The generation counter makes that
// clear stick: a request that started before the clear cannot write its
// now-stale response back in afterwards.

const MAX_ENTRIES = 8;

const entries = new Map<string, PropertiesResponse>();
let generation = 0;

export function propertiesCacheKey(userId: string | null | undefined, countyId: string): string {
  return `${userId || "anon"}|${countyId}`;
}

/** Returns the cached response for key, marking it most recently used. */
export function getCachedProperties(key: string): PropertiesResponse | undefined {
  const hit = entries.get(key);
  if (hit) {
    entries.delete(key);
    entries.set(key, hit);
  }
  return hit;
}

/** The current generation. Capture it when a request starts. */
export function propertiesCacheGeneration(): number {
  return generation;
}

/**
 * Stores a response for key, unless the cache was cleared after the request
 * started (startedAtGeneration is stale) — that response describes a state the
 * clear was meant to discard.
 */
export function setCachedProperties(
  key: string,
  value: PropertiesResponse,
  startedAtGeneration: number,
): void {
  if (startedAtGeneration !== generation) return;
  entries.delete(key);
  entries.set(key, value);
  while (entries.size > MAX_ENTRIES) {
    const oldest = entries.keys().next().value;
    if (oldest === undefined) break;
    entries.delete(oldest);
  }
}

/**
 * Drops every entry and invalidates any request already in flight. Also
 * clears the persisted map pins (pinStore), which describe the same server
 * state this clear is discarding.
 */
export function clearPropertiesCache(): void {
  entries.clear();
  generation++;
  clearPersistedPins();
}

export const PROPERTIES_CACHE_MAX_ENTRIES = MAX_ENTRIES;
